// Motor de bitácora UCI (Fase 4, Tanda 3) — `EscalaClinica`: escalas con
// puntaje + interpretación (Glasgow, RASS, Ramsay, Mottling,
// Braden-Bergstom, Evans, TISS-28, dolor sedado...) calculadas en código,
// no dejadas como campo libre (decisión de arquitectura explícita en
// PLAN.md). Todavía sin conectar a ningún formulario real — ver
// `TablaHoraria24h.tsx` para la nota completa.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Par } from '../index';

export interface OpcionEscalaClinica {
  readonly valor: number;
  readonly etiqueta: string;
}

export interface ItemEscalaClinica {
  readonly clave: string;
  readonly etiqueta: string;
  readonly opciones: readonly OpcionEscalaClinica[];
}

/** Rango cerrado [min, max] → etiqueta de interpretación (ej. Glasgow 13-15 "Leve"). El primer rango que matchea gana — se espera que el catálogo de cada escala real los deje sin solapar. */
export interface RangoInterpretacionEscala {
  readonly min: number;
  readonly max: number;
  readonly etiqueta: string;
}

export interface DefinicionEscalaClinica {
  readonly items: readonly ItemEscalaClinica[];
  readonly rangos: readonly RangoInterpretacionEscala[];
}

export type RespuestasEscalaClinica = Readonly<Record<string, number | undefined>>;

/**
 * Suma los puntos de todos los ítems — `undefined` si falta responder
 * cualquiera de ellos (una escala clínica a medio llenar NO tiene un total
 * parcial válido; mostrar un número ahí sería inventar un puntaje que
 * nadie eligió).
 */
export function calcularTotalEscala(definicion: DefinicionEscalaClinica, respuestas: RespuestasEscalaClinica): number | undefined {
  let total = 0;
  for (const item of definicion.items) {
    const valor = respuestas[item.clave];
    if (valor === undefined) return undefined;
    total += valor;
  }
  return total;
}

/** `undefined` si el total no está disponible, o si ningún rango del catálogo lo cubre (defensivo — no debería pasar con un catálogo bien armado). */
export function interpretarEscala(definicion: DefinicionEscalaClinica, total: number | undefined): string | undefined {
  if (total === undefined) return undefined;
  return definicion.rangos.find((r) => total >= r.min && total <= r.max)?.etiqueta;
}

/** Ítems respondidos + total/interpretación (si están completos) de una `EscalaClinica` — pura, exportada para test. Reusada por cualquier formulario que consuma este motor (UCI, 016A...). */
export function paresDeEscalaClinica(definicion: DefinicionEscalaClinica, respuestas: RespuestasEscalaClinica, tituloTotal: string): Par[] {
  const pares: Par[] = definicion.items
    .map((item) => {
      const valor = respuestas[item.clave];
      const opcion = valor !== undefined ? item.opciones.find((o) => o.valor === valor) : undefined;
      return { pregunta: item.etiqueta, respuesta: opcion?.etiqueta ?? '' };
    })
    .filter((p) => p.respuesta !== '');
  const total = calcularTotalEscala(definicion, respuestas);
  if (total !== undefined) {
    const interpretacion = interpretarEscala(definicion, total);
    pares.push({ pregunta: tituloTotal, respuesta: interpretacion !== undefined ? `${total} — ${interpretacion}` : String(total) });
  }
  return pares;
}

export interface EscalaClinicaProps {
  readonly definicion: DefinicionEscalaClinica;
  readonly respuestas: RespuestasEscalaClinica;
  readonly onChange: (respuestas: RespuestasEscalaClinica) => void;
  /** Prefijo de los `id` de los selects — obligatorio de facto cuando hay más de una escala con los mismos `clave` en la misma página (ej. Apgar por minuto en 016). */
  readonly idPrefix?: string;
}

/** Escala con puntaje + interpretación calculados en código — reusada por Glasgow/RASS/Ramsay/Mottling/Braden-Bergstom/Evans/TISS-28/dolor sedado. */
export function EscalaClinica({ definicion, respuestas, onChange, idPrefix = 'escala' }: EscalaClinicaProps): JSX.Element {
  const { t } = useTranslation();
  const total = calcularTotalEscala(definicion, respuestas);
  const interpretacion = interpretarEscala(definicion, total);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-hce-surface p-3">
      {definicion.items.map((item) => (
        <div key={item.clave} className="flex flex-col gap-1">
          <Label htmlFor={`${idPrefix}-${item.clave}`}>{item.etiqueta}</Label>
          <Select
            value={respuestas[item.clave] !== undefined ? String(respuestas[item.clave]) : undefined}
            onValueChange={(v) => onChange({ ...respuestas, [item.clave]: Number(v) })}
          >
            <SelectTrigger id={`${idPrefix}-${item.clave}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {item.opciones.map((op) => (
                <SelectItem key={op.valor} value={String(op.valor)}>
                  {op.etiqueta}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-medium text-hce-ink">
        <span>{t('formularios.uci.escalaClinica.total')}</span>
        <span>{total ?? '—'}</span>
      </div>
      {interpretacion && <p className="text-sm text-hce-muted">{interpretacion}</p>}
    </div>
  );
}
